import { useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../../components/base/Button';
import Input from '../../../components/base/Input';
import { categories, locations } from '../../../mocks/categories';

export default function HeroSection() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);

  const handleSearch = () => {
    // Navigate to listings with search parameters
    const params = new URLSearchParams();
    if (searchQuery) params.set('q', searchQuery);
    if (selectedCategory) params.set('category', selectedCategory);
    if (selectedLocation) params.set('location', selectedLocation);
    
    window.REACT_APP_NAVIGATE(`/listings?${params.toString()}`);
  };

  return (
    <section 
      className="relative bg-cover bg-center bg-no-repeat min-h-[600px] flex items-center"
      style={{
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://public.readdy.ai/ai/img_res/d9c20895581490636c5cd96cb0a637fa.jpg')`
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="text-center text-white">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Buy & Sell <span className="text-green-400">Anything</span><br />
            in Nigeria
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-gray-200 max-w-3xl mx-auto">
            Join thousands of Nigerians buying and selling on Nigeria's most trusted marketplace. 
            From Lagos to Abuja, find what you need or sell what you have.
          </p>

          {/* Search Bar */}
          <div className="max-w-4xl mx-auto mb-8">
            <div className="bg-white rounded-lg p-4 shadow-lg">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Search Input */}
                <div className="md:col-span-2">
                  <Input
                    type="text"
                    placeholder="What are you looking for?"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-12 text-base"
                  />
                </div>

                {/* Category Dropdown */}
                <div className="relative">
                  <button
                    className="w-full h-12 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-left text-sm bg-white flex items-center justify-between"
                    onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                  >
                    <span className={selectedCategory ? 'text-gray-900' : 'text-gray-500'}>
                      {selectedCategory || 'All Categories'}
                    </span>
                    <i className="ri-arrow-down-s-line text-gray-400"></i>
                  </button>
                  {showCategoryDropdown && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto">
                      <button
                        className="w-full px-3 py-2 text-left hover:bg-gray-50 text-sm"
                        onClick={() => {
                          setSelectedCategory('');
                          setShowCategoryDropdown(false);
                        }}
                      >
                        All Categories
                      </button>
                      {categories.map((category) => (
                        <button
                          key={category.id}
                          className="w-full px-3 py-2 text-left hover:bg-gray-50 text-sm flex items-center"
                          onClick={() => {
                            setSelectedCategory(category.name);
                            setShowCategoryDropdown(false);
                          }}
                        >
                          <i className={`${category.icon} mr-2 text-gray-400`}></i>
                          {category.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Location Dropdown */}
                <div className="relative">
                  <button
                    className="w-full h-12 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-left text-base font-medium bg-white flex items-center justify-between"
                    onClick={() => setShowLocationDropdown(!showLocationDropdown)}
                  >
                    <span className={selectedLocation ? 'text-gray-900 font-semibold' : 'text-gray-600 font-medium'}>
                      {selectedLocation || 'All Locations'}
                    </span>
                    <i className="ri-arrow-down-s-line text-gray-400"></i>
                  </button>
                  {showLocationDropdown && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto">
                      <button
                        className="w-full px-3 py-2 text-left hover:bg-gray-50 text-base font-medium text-gray-900"
                        onClick={() => {
                          setSelectedLocation('');
                          setShowLocationDropdown(false);
                        }}
                      >
                        All Locations
                      </button>
                      {locations.map((location) => (
                        <button
                          key={location}
                          className="w-full px-3 py-2 text-left hover:bg-gray-50 text-base font-medium text-gray-900 flex items-center"
                          onClick={() => {
                            setSelectedLocation(location);
                            setShowLocationDropdown(false);
                          }}
                        >
                          <i className="ri-map-pin-line mr-2 text-gray-400"></i>
                          {location}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-4">
                <Button
                  variant="primary"
                  size="lg"
                  className="w-full md:w-auto px-8"
                  onClick={handleSearch}
                >
                  <i className="ri-search-line mr-2"></i>
                  Search Listings
                </Button>
              </div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/post-ad">
              <Button variant="primary" size="lg" className="px-8">
                <i className="ri-add-line mr-2"></i>
                Post Your Ad
              </Button>
            </Link>
            <Link to="/listings">
              <Button variant="outline" size="lg" className="px-8 bg-white/10 border-white text-white hover:bg-white hover:text-gray-900">
                <i className="ri-eye-line mr-2"></i>
                Browse Listings
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}